/**
 * "How to do it" (як треба): after a fall, a cased jump, a drill under 3 stars or a slow sector, a card says what
 * went wrong and what to do, and the expert demo for that spot AUTO-PLAYS inside the card (owner decision E): the
 * main 3D view runs the demo behind a dimmed frame, like a video in the modal, looping, with the input overlay (gas,
 * front / rear brake, body, stance, foot), markers where it matters ("brake here", "gas over the lip") and slow
 * motion around them. Replay, Slow-mo, Try again from here, Close. The ride is put aside and restored exactly
 * (src/app/howto.ts DemoPlayer snapshots the world). Offered once per situation per session; while riding it is a
 * small prompt that never stops the ride.
 */
import './howto.css';
import type { DemoOverlay } from '../app/howto';
import type { DemoClip, DemoMarkKind } from '../game/howto/demo';
import type { ControlMode } from '../game/howto/clipfile';
import { Situation, situationKey } from '../game/howto/situation';
import { MessageKey } from '../i18n';
import { h, icon } from './dom';
import { btn } from './screens';
import type { GameUi } from './ui';

/** First release: the three drills and the Zielonka jumps (berms, enduro obstacles, slow sectors come later). */
const shipped = (s: Situation) => !!s.drill || (s.level === 'track' && !!s.feature?.startsWith('jump@'));

/** Seconds the in-ride prompt stays up. */
const PROMPT_TIME = 9;

type Phase = 'idle' | 'prompt' | 'card' | 'demo';

export class HowToUi {
  phase: Phase = 'idle';
  situation: Situation | null = null;
  slow = false;
  private clip: DemoClip | null | undefined;
  private readonly offered = new Set<string>();
  private promptTimer = 0;
  private pausedForIt = false;
  private readonly prompt: HTMLElement;
  private readonly overlay: HTMLElement;
  private readonly bars: Record<'throttle' | 'front' | 'rear', HTMLElement>;
  private readonly bodyDot: HTMLElement;
  private readonly stance: HTMLElement;
  private readonly foot: HTMLElement;
  private readonly progress: HTMLElement;
  private readonly pins: HTMLElement;
  private readonly pinEls = new Map<DemoMarkKind, HTMLElement>();
  /** The input panel: lives in the overlay, moved into the card's video frame while the card is up. */
  private readonly inputs: HTMLElement;

  constructor(private readonly ui: GameUi) {
    this.prompt = h('button.howto-prompt.glass-hud', { attrs: { type: 'button', hidden: true }, on: { click: () => this.open() } });
    const bar = (k: 'throttle' | 'front' | 'rear') => h(`div.howto-bar.${k}`, {}, [h('i'), h('span', { text: ui.tr(`howto.in.${k}`) })]);
    this.bars = { throttle: bar('throttle'), front: bar('front'), rear: bar('rear') };
    this.bodyDot = h('i.dot');
    this.stance = h('span.howto-chip');
    this.foot = h('span.howto-chip.foot', { text: ui.tr('howto.in.foot') });
    this.progress = h('i');
    this.pins = h('div.howto-pins');
    this.overlay = h('div.howto-overlay', { attrs: { hidden: true } }, [
      this.pins,
      h('div.howto-inputs.glass-hud', {}, [
        h('div.howto-badge', {}, [h('i.rec'), h('span.howto-demo-title', { text: ui.tr('howto.demo') })]),
        h('div.howto-bars', {}, [this.bars.throttle, this.bars.front, this.bars.rear]),
        h('div.howto-body', {}, [h('span', { text: ui.tr('howto.in.body') }), h('div.track', {}, [this.bodyDot]),
          h('div.ends', {}, [h('small', { text: ui.tr('howto.in.back') }), h('small', { text: ui.tr('howto.in.forward') })])]),
        h('div.howto-chips', {}, [this.stance, this.foot]),
        h('div.howto-progress', {}, [this.progress]),
      ]),
    ]);
    this.inputs = this.overlay.querySelector<HTMLElement>('.howto-inputs')!;
    document.body.append(this.prompt, this.overlay);
    addEventListener('keydown', (e) => this.onKey(e));
  }

  /** Something went wrong. Offered once per situation per session; `force` when the player asked. */
  offer(s: Situation, force = false): void {
    const key = situationKey(s);
    if (!shipped(s)) return;
    if (!force && (this.offered.has(key) || !this.ui.settings.hints)) return;
    if (this.phase === 'card' || this.phase === 'demo') return; // one at a time, never over a demo
    this.offered.add(key);
    this.situation = s;
    this.clip = undefined;
    this.pending = null;
    void this.loadClip();
    // On the results the card would cover Try again / Watch replay / Create video (QA 2026-10-03 B12): the prompt
    // chip is offered there as in a ride, and opens the card when tapped.
    if (this.ui.state.screen === 'play' || this.ui.state.screen === 'results') this.showPrompt();
    else this.open();
  }

  private showPrompt(): void {
    const s = this.situation!;
    this.phase = 'prompt';
    this.prompt.replaceChildren(
      h('span.howto-prompt-title', {}, [icon('play'), h('span', { text: this.ui.tr('howto.title') })]),
      h('span.howto-prompt-text', { text: this.ui.tr(causeKey(s, 'what')) }),
      h('kbd', { text: this.ui.touch ? this.ui.tr('howto.show') : `H · ${this.ui.tr('howto.show')}` }),
    );
    this.prompt.hidden = false;
    this.promptTimer = PROMPT_TIME;
  }

  private hidePrompt(): void { this.prompt.hidden = true; if (this.phase === 'prompt') this.phase = 'idle'; }

  /** Per rendered frame (prompt timeout, demo overlay). */
  frame(dt: number, demo: DemoOverlay | null): void {
    if (this.phase === 'prompt') {
      this.promptTimer -= dt;
      if (this.promptTimer <= 0 || (this.ui.state.screen !== 'play' && this.ui.state.screen !== 'results')) {
        // Pausing while it is up opens the card (the gamepad's way in); otherwise it just goes.
        if (this.ui.state.screen === 'pause' && this.promptTimer > 0) this.open(); else this.hidePrompt();
      }
    }
    this.overlay.hidden = this.phase !== 'demo' || !demo;
    if (this.phase === 'demo' && demo) this.drawOverlay(demo);
  }

  /** The full card (a modal: keyboard and gamepad navigate its buttons). Pauses a ride in progress. */
  open(): void {
    if (!this.situation) return;
    this.hidePrompt();
    if (this.ui.state.screen === 'play') { this.pausedForIt = true; this.ui.dispatch({ type: 'pause' }); }
    this.phase = 'card';
    this.ui.render();
    this.show(); // auto-play: no "Show me" press
  }

  close(): void {
    if (this.phase === 'idle') return;
    this.ui.host?.stopDemo?.();
    this.hidePrompt();
    this.phase = 'idle';
    this.ui.render();
    if (this.pausedForIt && this.ui.state.screen === 'pause') this.ui.dispatch({ type: 'resume' });
    this.pausedForIt = false;
  }

  private pending: Promise<DemoClip | null> | null = null;

  /** Starts loading the clip (prefetched during the ride, so this is normally already settled). */
  private loadClip(): Promise<DemoClip | null> {
    const s = this.situation;
    if (!s) return Promise.resolve(null);
    if (!this.pending) {
      const mode = controlMode(this.ui.settings.beginner);
      const host = this.ui.host;
      const specific = host?.howtoClip?.(s, mode) ?? Promise.resolve(null);
      // A jump or a drill whose exact cause has no clip still gets the canonical demo for that spot (the clean
      // reference ride over the jump / through the drill): the card always has a video to teach from, shown "how it
      // should be done on this example", instead of the text-only "no demo for this spot" card.
      const canonical: Situation | null = s.feature ? { ...s, cause: 'cased' } : s.drill ? { ...s, cause: 'fell' } : null;
      const useCanonical = canonical && situationKey(canonical) !== situationKey(s);
      this.pending = specific.then((c) => (c || !useCanonical || !canonical ? c : host?.howtoClip?.(canonical, mode) ?? null));
    }
    const p = this.pending;
    void p.then((c) => {
      if (this.situation !== s || this.clip !== undefined) return;
      this.clip = c;
      if (this.phase === 'card') this.ui.render();
    });
    return p;
  }

  private demoClip(): DemoClip | null { return this.clip ?? null; }

  show(): void {
    const s = this.situation;
    void this.loadClip().then((clip) => {
      if (this.situation !== s || (this.phase !== 'card' && this.phase !== 'demo')) return;
      this.clip = clip;
      if (!clip) { this.ui.render(); return; }
      this.startDemo(clip);
    });
  }

  private startDemo(clip: DemoClip): void {
    this.ui.host?.playDemo?.(clip, this.slow);
    this.phase = 'demo';
    this.ui.render();
  }

  toggleSlow(): void {
    this.slow = !this.slow;
    this.ui.host?.setDemoSlow?.(this.slow);
    this.ui.render();
  }

  /** The demo reached its end: it loops (the card stays up until Close or Try again). */
  demoEnded(): void {
    if (this.phase !== 'demo' || !this.clip) return;
    this.ui.host?.playDemo?.(this.clip, this.slow);
  }

  tryHere(): void {
    const clip = this.demoClip(), s = this.situation;
    this.ui.host?.stopDemo?.();
    this.phase = 'idle';
    this.pausedForIt = false;
    if (s?.drill) { this.ui.render(); this.ui.dispatch({ type: 'restart' }); return; }
    if (clip) this.ui.host?.practiceFrom?.(clip);
    this.ui.render();
    if (this.ui.state.screen === 'pause') this.ui.dispatch({ type: 'resume' });
  }

  /** Esc / B: close the card (and its demo). Returns false when there was nothing to close. */
  back(): boolean {
    if (this.phase === 'demo' || this.phase === 'card') { this.close(); return true; }
    return false;
  }

  get active(): boolean { return this.phase === 'card' || this.phase === 'demo'; }

  private onKey(e: KeyboardEvent): void {
    if (e.repeat) return;
    if (e.code === 'KeyH' && this.phase === 'prompt') { e.preventDefault(); this.open(); return; }
    if (this.phase !== 'demo') return;
    if (e.code === 'KeyS') { e.preventDefault(); this.toggleSlow(); }
    if (e.code === 'KeyR') { e.preventDefault(); this.show(); }
  }

  /** The card or the demo bar, rendered into the UI root by GameUi.render. */
  render(): HTMLElement | null {
    const s = this.situation, ui = this.ui;
    if (!s || !this.active) return null;
    const slowBtn = btn(ui.tr('howto.slow'), () => this.toggleSlow(), { ico: 'timer', cls: this.slow ? 'on' : undefined });
    slowBtn.setAttribute('aria-pressed', String(this.slow));
    const clip = this.clip; // undefined = still loading (prefetched during the ride, so normally ready)
    if (clip !== null) {
      // The live card: a video-like frame the 3D demo shows through (the rest dimmed), the input panel inside it,
      // what to do and the controls under it.
      const frame = h('div.howto-frame', {}, [
        h('div.howto-frame-badge', {}, [h('i.rec'), h('span', { text: ui.tr('howto.demo') }), this.slow ? h('b', { text: ui.tr('howto.slow') }) : null]),
        this.phase === 'demo' ? null : h('p.howto-frame-wait', { text: ui.tr('howto.loading') }),
      ]);
      if (this.phase === 'demo') frame.append(this.inputs); else this.overlay.querySelector('.howto-pins')!.after(this.inputs);
      return h('div.modal.howto-live', { attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': ui.tr('howto.title') } }, [
        h('div.howto-live-head', {}, [
          h('div.eyebrow', {}, [icon('info'), h('span', { text: ui.tr('howto.where', { place: placeName(ui, s) }) })]),
          h('h2.howto-title', { text: ui.tr('howto.title') }),
        ]),
        frame,
        h('div.panel.glass.howto-live-card', {}, [
          h('div.howto-live-rows', {}, [
            h('div.howto-row.wrong', {}, [h('h3', { text: ui.tr('howto.wrong') }), h('p', { text: ui.tr(causeKey(s, 'what')) })]),
            h('div.howto-row.do', {}, [h('h3', { text: ui.tr('howto.do') }), h('p', { text: ui.tr(causeKey(s, 'do')) })]),
          ]),
          h('div.howto-actions', {}, [
            btn(ui.tr('howto.again'), () => this.show(), { ico: 'replay' }),
            slowBtn,
            h('button.btn.primary', { attrs: { 'data-nav': '', autofocus: true }, on: { click: () => this.tryHere() } }, [icon('retry'), h('span', { text: ui.tr('howto.tryHere') })]),
            btn(ui.tr('ui.close'), () => this.close(), { ico: 'close' }),
          ]),
        ]),
      ]);
    }
    return h('div.modal.howto-modal', { attrs: { role: 'dialog', 'aria-modal': 'true' }, on: { click: (e) => { if (e.target === e.currentTarget) this.close(); } } }, [
      h('div.panel.glass.howto-card', {}, [
        h('div.eyebrow', {}, [icon('info'), h('span', { text: ui.tr('howto.where', { place: placeName(ui, s) }) })]),
        h('h2.howto-title', { text: ui.tr('howto.title') }),
        h('div.howto-row.wrong', {}, [h('h3', { text: ui.tr('howto.wrong') }), h('p', { text: ui.tr(causeKey(s, 'what')) })]),
        h('div.howto-row.do', {}, [h('h3', { text: ui.tr('howto.do') }), h('p', { text: ui.tr(causeKey(s, 'do')) })]),
        h('div.howto-row.keys', {}, [h('h3', { text: ui.tr('howto.keys') }), h('p', { text: ui.tr(causeKey(s, 'keys')) })]),
        h('p.muted', { text: ui.tr('howto.unavailable') }),
        h('div.howto-actions', {}, [
          btn(ui.tr('howto.tryHere'), () => this.tryHere(), { ico: 'retry' }),
          btn(ui.tr('howto.later'), () => this.close(), { ico: 'close' }),
        ]),
      ]),
    ]);
  }

  private drawOverlay(o: DemoOverlay): void {
    const set = (el: HTMLElement, v: number) => el.style.setProperty('--v', v.toFixed(3));
    set(this.bars.throttle, o.throttle);
    set(this.bars.front, Math.min(1, o.front));
    set(this.bars.rear, Math.min(1, o.rear));
    this.bodyDot.style.setProperty('--b', o.body.toFixed(3));
    const st = this.ui.tr(o.standing ? 'howto.in.stand' : 'howto.in.sit');
    if (this.stance.textContent !== st) this.stance.textContent = st;
    this.stance.classList.toggle('standing', o.standing);
    this.foot.classList.toggle('on', o.foot);
    this.progress.style.width = `${(o.progress * 100).toFixed(1)}%`;
    const seen = new Set<DemoMarkKind>();
    for (const m of o.marks) {
      if (!m.visible || seen.has(m.kind)) continue;
      seen.add(m.kind);
      let el = this.pinEls.get(m.kind);
      if (!el) {
        el = h(`div.howto-pin.${m.kind}`, {}, [h('span', { text: this.ui.tr(`howto.mark.${m.kind}` as MessageKey) }), h('i')]);
        this.pinEls.set(m.kind, el);
        this.pins.append(el);
      }
      el.hidden = false;
      el.style.left = `${(m.x * 100).toFixed(2)}%`;
      el.style.top = `${(m.y * 100).toFixed(2)}%`;
      el.style.opacity = m.fade.toFixed(2);
    }
    for (const [k, el] of this.pinEls) if (!seen.has(k)) el.hidden = true;
  }
}

/**
 * The expert rides in the player's control mode: simplified mode (settings.beginner) sends every frame with the
 * assists and the automatic foot on (src/app/main.ts readInput), so the demo is recorded the same way.
 */
export const controlMode = (beginner: boolean): ControlMode => (beginner ? 'simplified' : 'real');

const causeKey = (s: Situation, part: 'what' | 'do' | 'keys') => `howto.cause.${s.cause}.${part}` as MessageKey;

function placeName(ui: GameUi, s: Situation): string {
  if (s.drill) return ui.tr(`drill.${s.drill}.name` as MessageKey);
  const f = s.feature;
  if (!f) return ui.tr('howto.place.track');
  const [kind, at] = f.split('@') as [string, string];
  if (kind === 'sector') return ui.tr('howto.place.sector', { n: at });
  const name = kind === 'jump' ? ui.tr('howto.place.jump') : ui.tr(`ui.feat.one.${kind}` as MessageKey);
  return `${name}, ${at} m`;
}
